import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../App";
import Layout from "../components/Layout";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Store, Download, Upload, Loader2, CheckCircle2, Trash2, Package, Atom, Plane, Shield, Factory, FlaskConical, Flame } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../components/shared/PageHeader";

const ICONS = { Atom, Plane, Shield, Factory, FlaskConical, Flame };
const CAT_COLORS = { nuclear: "#DC2626", aerospace: "#2563EB", defense: "#0F172A", manufacturing: "#D97706", pharma: "#10B981", energy: "#F59E0B" };

export default function MarketplacePage({ onLogout, userRole, isFeatureEnabled }) {
  const [catalog, setCatalog] = useState([]);
  const [installed, setInstalled] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("catalog");
  const [exportOpen, setExportOpen] = useState(false);
  const [exportData, setExportData] = useState(null);
  const isAdmin = userRole === "admin";

  const fetchAll = useCallback(async () => {
    try {
      const [cR, iR, sR] = await Promise.allSettled([
        axios.get(`${API}/marketplace/catalog`, { headers: getAuthHeaders() }),
        axios.get(`${API}/marketplace/installed`, { headers: getAuthHeaders() }),
        axios.get(`${API}/marketplace/stats`, { headers: getAuthHeaders() }),
      ]);
      if (cR.status === "fulfilled") setCatalog(cR.value.data || []);
      if (iR.status === "fulfilled") setInstalled(iR.value.data || []);
      if (sR.status === "fulfilled") setStats(sR.value.data || {});
    } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchAll(); }, [fetchAll]);

  const installPlugin = async (pluginId) => {
    try { await axios.post(`${API}/marketplace/install/${pluginId}`, {}, { headers: getAuthHeaders() }); toast.success("Plugin installed"); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const uninstallPlugin = async (pluginId) => {
    if (!window.confirm("Uninstall this plugin?")) return;
    try { await axios.post(`${API}/marketplace/uninstall/${pluginId}`, {}, { headers: getAuthHeaders() }); toast.success("Plugin uninstalled"); fetchAll(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const exportConfig = async () => {
    try { const r = await axios.get(`${API}/marketplace/export-config`, { headers: getAuthHeaders() }); setExportData(r.data); setExportOpen(true); }
    catch { toast.error("Export failed"); }
  };
  const downloadExport = () => {
    if (!exportData) return;
    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `vault-config-${new Date().toISOString().split("T")[0]}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}><div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#2563EB]" size={28} /></div></Layout>;

  return (
    <Layout onLogout={onLogout} userRole={userRole} isFeatureEnabled={isFeatureEnabled}>
      <div className="space-y-6" data-testid="marketplace-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <PageHeader title="Deployment Marketplace" subtitle="Industry plugins, configuration templates, and extensions" testId="marketplace-title" />
          <div className="flex gap-2">
            {isAdmin && <Button onClick={exportConfig} variant="outline" className="rounded-none text-xs font-bold border-[#2563EB] text-[#2563EB]" data-testid="export-btn"><Download size={14} className="mr-1" /> Export Config</Button>}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-3" data-testid="marketplace-stats">
          <div className="bg-white border border-[#E2E8F0] px-4 py-3"><p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Catalog</p><p className="text-2xl font-heading font-black text-[#0F172A]">{stats.catalog_count || 0}</p></div>
          <div className="bg-white border border-[#E2E8F0] px-4 py-3"><p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Installed</p><p className="text-2xl font-heading font-black text-[#10B981]">{stats.installed_count || 0}</p></div>
          <div className="bg-white border border-[#E2E8F0] px-4 py-3"><p className="text-[10px] font-bold uppercase tracking-wider text-[#94A3B8]">Categories</p><p className="text-2xl font-heading font-black text-[#7C3AED]">{(stats.categories || []).length}</p></div>
        </div>

        <div className="flex gap-1">
          {[["catalog", `Catalog (${catalog.length})`], ["installed", `Installed (${installed.length})`]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} className={`px-5 py-2.5 text-xs font-bold tracking-wider border ${tab === k ? "bg-[#0F172A] text-white border-[#0F172A]" : "bg-white text-[#64748B] border-[#E2E8F0]"}`} data-testid={`tab-${k}`}>{l}</button>
          ))}
        </div>

        {tab === "catalog" && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="catalog-grid">
            {catalog.map(p => {
              const Icon = ICONS[p.icon] || Package;
              const catColor = CAT_COLORS[p.category] || "#64748B";
              return (
                <div key={p.id} className="bg-white border border-[#E2E8F0] p-5 hover:border-[#94A3B8] transition-colors" data-testid={`plugin-${p.id}`}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 flex items-center justify-center border-2" style={{ borderColor: catColor, color: catColor }}><Icon size={20} /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-bold text-[#0F172A]">{p.name}</p>
                      <p className="text-[10px] font-bold uppercase mt-0.5" style={{ color: catColor }}>{p.category} · v{p.version}</p>
                    </div>
                  </div>
                  <p className="text-xs text-[#64748B] mt-2">{p.description}</p>
                  <div className="flex gap-1 mt-2 flex-wrap">{p.includes.map(inc => <span key={inc} className="text-[9px] px-1.5 py-0.5 bg-[#F8FAFC] border border-[#E2E8F0] text-[#64748B]">{inc}</span>)}</div>
                  <div className="mt-3">
                    {p.installed ? (
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] text-[#10B981] font-bold flex items-center gap-1"><CheckCircle2 size={12} />Installed</span>
                        {isAdmin && <Button size="sm" variant="outline" onClick={() => uninstallPlugin(p.id)} className="rounded-none border-[#FECACA] text-[#DC2626] text-[10px] ml-auto" data-testid={`uninstall-${p.id}`}><Trash2 size={11} className="mr-1" />Remove</Button>}
                      </div>
                    ) : (
                      isAdmin && <Button size="sm" onClick={() => installPlugin(p.id)} className="rounded-none bg-[#2563EB] text-white text-[10px] font-bold w-full" data-testid={`install-${p.id}`}><Download size={11} className="mr-1" />Install</Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {tab === "installed" && (
          <div className="bg-white border border-[#E2E8F0]" data-testid="installed-list">
            {installed.length === 0 ? <div className="p-12 text-center text-sm text-[#94A3B8]"><Store size={36} className="mx-auto mb-2 text-[#CBD5E1]" />No plugins installed yet.</div> : (
              <div className="divide-y divide-[#F1F5F9]">
                {installed.map(p => (
                  <div key={p.id} className="px-4 py-3 flex items-center justify-between hover:bg-[#FAFAFA]" data-testid={`installed-${p.plugin_id}`}>
                    <div>
                      <div className="flex items-center gap-2"><span className="font-bold text-sm text-[#0F172A]">{p.name}</span><span className="text-[10px] text-[#10B981] font-bold">{p.status}</span></div>
                      <p className="text-[11px] text-[#64748B]">v{p.version} · {p.category} · Installed by {p.installed_by_name} · {new Date(p.installed_at).toLocaleDateString()}</p>
                    </div>
                    {isAdmin && <Button size="sm" variant="outline" onClick={() => uninstallPlugin(p.plugin_id)} className="rounded-none border-[#FECACA] text-[#DC2626] text-[10px]"><Trash2 size={11} className="mr-1" />Remove</Button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Export Dialog */}
      <Dialog open={exportOpen} onOpenChange={setExportOpen}>
        <DialogContent className="bg-white rounded-none border border-[#E2E8F0] max-w-lg" data-testid="export-dialog">
          <DialogHeader><DialogTitle>Export Configuration</DialogTitle><DialogDescription className="text-xs text-[#64748B]">Download your customization config as a shareable JSON template.</DialogDescription></DialogHeader>
          {exportData && (
            <div className="space-y-3 mt-2">
              <div className="text-xs text-[#64748B]">
                <p>Exported by: {exportData.exported_by}</p>
                <p>Dropdowns: {(exportData.config?.dropdowns || []).length}</p>
                <p>Roles: {(exportData.config?.roles || []).length}</p>
                <p>Dashboard configs: {(exportData.config?.dashboard || []).length}</p>
              </div>
              <div className="bg-[#0F172A] text-[#10B981] p-3 font-mono text-[10px] max-h-40 overflow-auto">{JSON.stringify(exportData.config, null, 2).substring(0, 1000)}...</div>
              <Button onClick={downloadExport} className="w-full bg-[#2563EB] text-white rounded-none text-xs font-bold" data-testid="download-export-btn"><Download size={14} className="mr-1" /> Download JSON</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
